import axios from "axios";
import {
  refreshAccessToken,
  redirectToLogin,
  forceLogout,
} from "./authSession";
import {
  getStoredAccessToken,
  isAccessTokenExpired,
  isRefreshTokenExpired,
} from "../utils/authStorage";

const shouldSkipAuthHandling = (url?: string) => {
  return (
    !!url &&
    (url.includes("/auth/login") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/forgot-password"))
  );
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://api.example.com",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    if (shouldSkipAuthHandling(config.url)) {
      return config;
    }

    if (isRefreshTokenExpired()) {
      forceLogout();
      redirectToLogin();
      return Promise.reject(new Error("Refresh token expired."));
    }

    // Add auth token from localStorage if available
    let token = getStoredAccessToken();
    if (!token || isAccessTokenExpired(token)) {
      token = await refreshAccessToken();
    }

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle global errors (e.g., unauthorized, network errors)
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest?._retry &&
      !shouldSkipAuthHandling(originalRequest?.url)
    ) {
      originalRequest._retry = true;

      const nextAccessToken = await refreshAccessToken();
      if (nextAccessToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `Bearer ${nextAccessToken}`;
        return api(originalRequest);
      }
    }

    if (error.response && error.response.status === 401) {
      forceLogout();
      redirectToLogin();
    }

    return Promise.reject(error);
  },
);

export default api;
